import { useCallback, useState } from 'react';
import { ArrowRight, WarningCircle } from 'shared/icons';
import Button from '../../ui/Button';
import { Mono } from '../../ui/Mono';
import { CodeLine } from './CollectorChrome';
import { PlayScanError } from './collectorFeedback';
import { FormatLocationPlain } from './collectorScan';
import {
    ITEM_STEP_DESTINATION,
    ITEM_STEP_ORIGIN,
    ParseServiceOrder,
    SKIP_INCIDENT_KINDS,
    ServiceOrderItemLocation,
    SkipServiceOrderItem,
} from './collectorServiceOrders';

/**
 * PULAR O ITEM — a saída para o item que não deu para fazer.
 *
 * ── Por que isto é uma TELA e não um botão ──────────────────────────────────
 * "Item problemático não trava a rota" (§6.5) é uma regra generosa: o operador
 * não fica preso num pallet que não está lá. Mas ela tem um custo — o item vira
 * `SKIPPED_ALERT`, abre incidente e alguém vai ter de resolver depois. Um
 * atalho fácil de tocar transformaria "não achei o pallet" no jeito rápido de
 * se livrar de um item difícil, e o Supervisor descobriria o problema só no
 * fim do dia, com a rota inteira pulada.
 *
 * Então pular custa três coisas, nesta ordem: sair do passo, DIZER O QUE
 * ACONTECEU (não há opção pré-selecionada) e confirmar num botão que descreve a
 * consequência. Nada disso é escondido — a porta está visível em todo passo,
 * inclusive na tela vermelha e na tela de espera, que é onde ela costuma ser
 * necessária. O que ela não é: o botão grande do rodapé.
 *
 * ── Por que o motivo é obrigatório ──────────────────────────────────────────
 * O incidente nasce vinculado ao item, e é ele que o Supervisor vai ler no
 * painel. "Pulado" sem tipo obriga alguém a ir ao chão perguntar; "Pallet
 * ausente na R01-B06-A2-P1" já é uma tarefa.
 */
const SkipItemPanel = ({ label, order, item, operator, onCancel, onSkipped }) => {
    const [kind, setKind] = useState(null);
    const [notes, setNotes] = useState('');
    const [busy, setBusy] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const from = ServiceOrderItemLocation(item, ITEM_STEP_ORIGIN);
    const to = ServiceOrderItemLocation(item, ITEM_STEP_DESTINATION);

    const handleConfirm = useCallback(async () => {
        if (!kind) {
            return;
        }
        setBusy(true);
        setErrorMessage(null);
        try {
            const payload = await SkipServiceOrderItem(order.id, item.id, {
                operatorCode: operator?.employee_code || null,
                kind,
                notes: notes.trim() || null,
            });
            onSkipped(ParseServiceOrder(payload));
        } catch (error) {
            PlayScanError();
            setErrorMessage(error?.message || label('wms.common.load_error'));
        } finally {
            setBusy(false);
        }
    }, [item, kind, label, notes, onSkipped, operator, order.id]);

    return (
        <div className="flex min-h-[70vh] flex-col gap-4">
            <div className="flex flex-1 flex-col justify-center gap-4">
                <WarningCircle size={48} className="mx-auto text-warning-text dark:text-warning-300" />
                <h2 className="text-center text-2xl font-black leading-tight text-black dark:text-white">
                    {label('wms.collector.skip_title')}
                </h2>

                {/* Qual item, dito do jeito que o operador enxerga: os dois
                    endereços. Sem isto, confirmar é confiar que a tela está no
                    item certo — e ela pode ter mudado enquanto ele andava. */}
                <div className="space-y-1 rounded-xl bg-mono-50 px-3 py-3 text-center dark:bg-mono-900">
                    <p className="text-xs uppercase tracking-wide text-mono-500 dark:text-mono-400">
                        {`${label('wms.collector.item_counter')} ${item?.seq ?? '—'}`}
                    </p>
                    <div className="flex items-center justify-center gap-2 text-sm font-semibold text-black dark:text-white">
                        <span className="truncate">{FormatLocationPlain(from, { label })}</span>
                        <ArrowRight size={18} className="shrink-0 text-mono-500" />
                        <span className="truncate">
                            {to ? FormatLocationPlain(to, { label }) : label('wms.collector.free_destination_short')}
                        </span>
                    </div>
                    {item?.lpn_code ? <CodeLine className="text-xs">{item.lpn_code}</CodeLine> : null}
                </div>

                <p className="text-center text-sm text-mono-600 dark:text-mono-400">
                    {label('wms.collector.skip_explain')}
                </p>

                <div className="space-y-2">
                    <p className="text-sm font-semibold uppercase tracking-wide text-mono-500 dark:text-mono-400">
                        {label('wms.collector.skip_kind')}
                    </p>
                    {SKIP_INCIDENT_KINDS.map((option) => {
                        const selected = kind === option;
                        return (
                            <button
                                key={option}
                                type="button"
                                onClick={() => setKind(option)}
                                aria-pressed={selected}
                                className={`w-full rounded-xl border-2 px-4 py-4 text-left text-lg font-bold transition-colors ${
                                    selected
                                        ? 'border-black bg-mono-100 text-black dark:border-white dark:bg-mono-800 dark:text-white'
                                        : 'border-mono-300 bg-white text-mono-700 dark:border-mono-700 dark:bg-mono-950 dark:text-mono-300'
                                }`}
                            >
                                {label(`wms.incident_kind.${option}`)}
                            </button>
                        );
                    })}
                </div>

                <div className="space-y-1">
                    <label
                        htmlFor="collector-skip-notes"
                        className="text-sm font-semibold uppercase tracking-wide text-mono-500 dark:text-mono-400"
                    >
                        {label('wms.collector.skip_notes')}
                    </label>
                    <textarea
                        id="collector-skip-notes"
                        rows={2}
                        value={notes}
                        onChange={(event) => setNotes(event.target.value)}
                        placeholder={label('wms.collector.skip_notes_placeholder')}
                        className="w-full rounded-xl border-2 border-mono-300 bg-white px-3 py-3 text-base text-black placeholder-mono-400 focus:border-black focus:outline-none dark:border-mono-700 dark:bg-mono-950 dark:text-white dark:placeholder-mono-600 dark:focus:border-white"
                    />
                </div>

                <p className="text-center text-xs text-mono-500 dark:text-mono-400">
                    {label('wms.service_order_item.skipped_help')}
                </p>

                {errorMessage ? (
                    <div
                        role="alert"
                        className="flex items-start gap-2 rounded-xl bg-danger-soft p-3 text-sm text-danger-text dark:bg-danger-900/30 dark:text-danger-300"
                    >
                        <WarningCircle size={18} className="mt-0.5 shrink-0" />
                        <span>{errorMessage}</span>
                    </div>
                ) : null}

                {operator?.employee_code ? (
                    <p className="text-center text-xs text-mono-500 dark:text-mono-400">
                        {`${label('wms.incidents.field.created_by')}: `}
                        <Mono>{operator.employee_code}</Mono>
                    </p>
                ) : null}
            </div>

            <div className="sticky bottom-0 space-y-2 pt-2">
                <Button
                    variant="danger"
                    size="lg"
                    className="w-full py-5 text-lg font-black"
                    onClick={handleConfirm}
                    loading={busy}
                    disabled={!kind}
                >
                    {label('wms.collector.skip_confirm')}
                </Button>
                <Button variant="secondary" size="lg" className="w-full" onClick={onCancel} disabled={busy}>
                    {label('wms.common.cancel')}
                </Button>
            </div>
        </div>
    );
};

export default SkipItemPanel;
